/**
 * @license Codistica
 *
 * Copyright (c) 2020, Codistica and its affiliates.
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE.md file in the root directory of this source tree.
 *
 * BUILD DATE: Fri Jul 10 2020 16:51:08 GMT+0200 (Central European Summer Time)
 */
import _Array$from from "@babel/runtime-corejs3/core-js-stable/array/from";
import _reduceInstanceProperty from "@babel/runtime-corejs3/core-js-stable/instance/reduce";
import _mapInstanceProperty from "@babel/runtime-corejs3/core-js-stable/instance/map";
import _bindInstanceProperty from "@babel/runtime-corejs3/core-js-stable/instance/bind";
import _newArrowCheck from "@babel/runtime-corejs3/helpers/esm/newArrowCheck";
import _Object$getOwnPropertyNames from "@babel/runtime-corejs3/core-js-stable/object/get-own-property-names";
import _slicedToArray from "@babel/runtime-corejs3/helpers/esm/slicedToArray";

/** @module react/components/bullet-dropdown */
import { elementUtils } from '@codistica/browser';
import React, { useRef, useState } from 'react';
import resetClassNames from '../../css/reset.module.scss';
import { withOnClickOutside } from '../../hocs/with-on-click-outside.js';
import { mergeClassNames } from '../../modules/merge-class-names.js';
import { mergeStyles } from '../../modules/merge-styles.js';
import componentClassNames from './index.module.scss';
var Div = withOnClickOutside('div');

/**
 * @typedef bulletDropdownPropsType
 * @property {string} [title='menu'] - Menu title.
 * @property {Object<string,string>} [items={}] - Menu items.
 * @property {boolean} [autoClose=false] - Auto close menu on click outside.
 * @property {('top'|'bottom'|null)} [autoSpacing=null] - Auto reserve space for menu opening.
 * @property {Object<string,*>} [style={}] - React prop.
 * @property {string} [className=''] - React prop.
 * @property {Object<string,*>} [customStyles={}] - Custom styles prop.
 * @property {Object<string,*>} [customClassNames={}] - Custom classNames prop.
 * @property {('default'|string|null)} [globalTheme='default'] - Global theme to be used.
 */

/**
 * @description A modern bullet dropdown list.
 * @param {bulletDropdownPropsType} props - Props.
 * @returns {Object<string,*>} React component.
 */
function BulletDropdown(props) {
  var _this = this,
      _context,
      _context2,
      _context3;

  var title = props.title,
      items = props.items,
      autoClose = props.autoClose,
      autoSpacing = props.autoSpacing,
      style = props.style,
      className = props.className,
      customStyles = props.customStyles,
      customClassNames = props.customClassNames,
      globalTheme = props.globalTheme;
  var rootRef = useRef(null);

  var _useState = useState(null),
      _useState2 = _slicedToArray(_useState, 2),
      open = _useState2[0],
      setOpen = _useState2[1];

  var Element = autoClose ? Div : 'div';

  var names = _Object$getOwnPropertyNames(items);

  var headerHeight = getHeaderHeight();
  var listHeight = getListHeight();
  var globalStyles = globalTheme ? BulletDropdown.globalStyles[globalTheme] || {} : {};
  var globalClassNames = globalTheme ? BulletDropdown.globalClassNames[globalTheme] || {} : {};
  var mergedStyles = {
    root: mergeStyles(globalStyles.root, customStyles.root, style, {
      justifyContent: autoSpacing ? autoSpacing === 'top' ? 'flex-end' : 'flex-start' : null,
      height: autoSpacing && open !== null ? headerHeight + listHeight : null
    }),
    bullet: mergeStyles(globalStyles.bullet, customStyles.bullet, {
      transform: open ? 'rotate(90deg)' : null
    }),
    title: mergeStyles(globalStyles.title, customStyles.title),
    item: mergeStyles(globalStyles.item, customStyles.item)
  };
  var mergedClassNames = {
    root: mergeClassNames(resetClassNames.root, componentClassNames.root, globalClassNames.root, customClassNames.root, className),
    bullet: mergeClassNames(componentClassNames.triangleRight, globalClassNames.bullet, customClassNames.bullet),
    title: mergeClassNames(componentClassNames.title, globalClassNames.title, customClassNames.title),
    item: mergeClassNames(componentClassNames.item, globalClassNames.item, customClassNames.item)
  };
  return /*#__PURE__*/React.createElement(Element, {
    ref: setRootRef,
    onClickOutside: autoClose ? _bindInstanceProperty(_context = function _context() {
      _newArrowCheck(this, _this);

      return open && setOpen(false);
    }).call(_context, this) : undefined,
    style: mergedStyles.root,
    className: mergedClassNames.root
  }, /*#__PURE__*/React.createElement("div", {
    onClick: _bindInstanceProperty(_context2 = function _context2() {
      _newArrowCheck(this, _this);

      return setOpen(!open);
    }).call(_context2, this),
    className: componentClassNames.header
  }, /*#__PURE__*/React.createElement("span", {
    style: mergedStyles.bullet,
    className: mergedClassNames.bullet
  }), /*#__PURE__*/React.createElement("span", {
    style: mergedStyles.title,
    className: mergedClassNames.title
  }, title)), /*#__PURE__*/React.createElement("div", {
    style: {
      height: open ? listHeight : null,
      opacity: open ? 1 : null
    },
    className: componentClassNames.list
  }, _mapInstanceProperty(names).call(names, _bindInstanceProperty(_context3 = function _context3(name, key) {
    var _this2 = this,
        _context4,
        _context5;

    _newArrowCheck(this, _this);

    return /*#__PURE__*/React.createElement("a", {
      key: key,
      href: items[name],
      target: '_blank',
      rel: 'noopener noreferrer',
      onFocus: _bindInstanceProperty(_context4 = function _context4() {
        _newArrowCheck(this, _this2);

        return setOpen(true);
      }).call(_context4, this),
      onBlur: _bindInstanceProperty(_context5 = function _context5() {
        _newArrowCheck(this, _this2);

        return setOpen(false);
      }).call(_context5, this),
      style: mergedStyles.item,
      className: mergedClassNames.item
    }, /*#__PURE__*/React.createElement("span", null, name));
  }).call(_context3, this))));
  /**
   * @description Save root element reference.
   * @param {Object<string,*>} ref - Root element reference.
   * @returns {void} Void.
   */

  function setRootRef(ref) {
    rootRef.current = ref;

    if (open === null) {
      setOpen(false);
    }
  }
  /**
   * @description Calculate header height.
   * @returns {number} Header height.
   */


  function getHeaderHeight() {
    if (!rootRef.current) {
      return 0;
    }

    return elementUtils.getHeight(rootRef.current.firstChild);
  }
  /**
   * @description Calculate list height based on children.
   * @returns {number} List height.
   */


  function getListHeight() {
    var _context6,
        _this3 = this,
        _context7;

    if (!rootRef.current) {
      return 0;
    }

    return _reduceInstanceProperty(_context6 = _Array$from(rootRef.current.lastChild.children)).call(_context6, _bindInstanceProperty(_context7 = function _context7(acc, elem) {
      _newArrowCheck(this, _this3);

      return acc + elementUtils.getHeight(elem);
    }).call(_context7, this), 0);
  }
}

BulletDropdown.defaultProps = {
  title: 'menu',
  items: {},
  autoClose: false,
  autoSpacing: null,
  style: {},
  className: '',
  customStyles: {},
  customClassNames: {},
  globalTheme: 'default'
};
BulletDropdown.globalStyles = {
  default: {
    root: {},
    bullet: {},
    title: {},
    item: {}
  }
};
BulletDropdown.globalClassNames = {
  default: {
    root: '',
    bullet: '',
    title: '',
    item: ''
  }
};
export { BulletDropdown };